import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../environments/environment';
import {
  StripeConnectAccountRow,
  StripeConnectLocationRow,
  StripeConnectSettingsService,
} from '../../services/stripe-connect-settings.service';
import { CollapsibleCardComponent } from '../collapsible-card/collapsible-card.component';
import { MatSelectModule } from '@angular/material/select';
import { SelectSearchComponent } from '../../shared/components/select-search/select-search.component';
import { SelectFilterPipe } from '../../shared/pipes/select-filter.pipe';

const STORAGE_KEY = 'way_payment_stripe_connect_selection';

/** Options for dropdown (full label visible, no native select truncation). */
export interface PaymentSelectOption {
  id: string;
  label: string;
}

interface StoredSelection {
  stripe_account_id: string;
  location_id: string;
  app_location_id?: string;
}

@Component({
  selector: 'app-payment-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CollapsibleCardComponent,
    MatSelectModule,
    SelectSearchComponent,
    SelectFilterPipe,
  ],
  templateUrl: './payment-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './payment-settings.component.scss',
})
export class PaymentSettingsComponent implements OnInit, OnChanges {
  /** Locations from profile (may include `stripe_account_id` for fallback grouping). */
  @Input() locations: Record<string, any>[] = [];

  readonly hasPublishableKey = !!environment.stripePublicKey?.trim();

  accounts: StripeConnectAccountRow[] = [];
  locationsForAccount: StripeConnectLocationRow[] = [];

  accountOptions: PaymentSelectOption[] = [];
  locationOptions: PaymentSelectOption[] = [];

  selectedAccountId = '';
  selectedLocationId = '';

  loadingAccounts = false;
  loadingLocations = false;
  saveMessage: { type: 'ok' | 'err'; text: string } | null = null;
  accountsSource: 'api' | 'locations' | 'none' = 'none';

  /** Restored placeholders kept only if user had selected values mid-session (not applied on init). */
  private pendingLocationId = '';
  private lastLoadedLocationsAccountId = '';

  get selectedHasTerminalLocation(): boolean {
    return /^tml_[a-zA-Z0-9]+$/.test(String(this.selectedLocationId || '').trim());
  }

  get canSavePaymentSettings(): boolean {
    return (
      !!this.selectedAccountId &&
      this.selectedHasTerminalLocation &&
      !this.loadingAccounts &&
      !this.loadingLocations
    );
  }

  get locationSelectPlaceholder(): string {
    if (!this.selectedAccountId) {
      return 'Select an account first…';
    }
    if (this.loadingLocations) {
      return 'Loading Terminal locations…';
    }
    if (this.locationOptions.length === 0) {
      return 'No Stripe Terminal location (tml_…) found';
    }
    return 'Select location…';
  }

  constructor(private stripeConnect: StripeConnectSettingsService) {}

  ngOnInit(): void {
    this.loadAccounts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['locations']) {
      return;
    }
    this.applyLocationDerivedAccounts();
  }

  private persistSelection(): void {
    try {
      const payload: StoredSelection = {
        stripe_account_id: this.selectedAccountId,
        location_id: this.selectedLocationId,
        app_location_id: this.resolveAppLocationId(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore */
    }
  }

  private buildAccountsFromProfileLocations(): StripeConnectAccountRow[] {
    const map = new Map<string, StripeConnectAccountRow>();
    for (const loc of this.locations || []) {
      const aid = String(loc['stripe_account_id'] ?? loc['stripeAccountId'] ?? '').trim();
      if (!aid) continue;
      const branch = String(loc['branch'] ?? '').trim();
      const customLabel = String(loc['stripe_account_label'] ?? '').trim();
      const existing = map.get(aid);
      if (!existing) {
        map.set(aid, {
          id: aid,
          stripe_account_id: aid,
          display_name: customLabel || branch || '',
          email: '',
        });
      } else if (!String(existing.display_name ?? '').trim()) {
        if (customLabel) {
          existing.display_name = customLabel;
        } else if (branch) {
          existing.display_name = branch;
        }
      }
    }
    return [...map.values()];
  }

  /** Only shop locations that already have a Terminal location id for this Connect account. */
  private buildTerminalLocationsFromApp(accountId: string): StripeConnectLocationRow[] {
    const rows: StripeConnectLocationRow[] = [];
    const seen = new Set<string>();
    for (const loc of this.locations || []) {
      const aid = String(loc['stripe_account_id'] ?? loc['stripeAccountId'] ?? '').trim();
      if (aid !== accountId) continue;
      const appId = String(loc['id'] ?? '').trim();
      const tml = String(
        loc['terminal_location_id'] ??
          loc['terminalLocationId'] ??
          loc['stripe_terminal_location_id'] ??
          ''
      ).trim();
      if (!appId || !/^tml_[a-zA-Z0-9]+$/.test(tml) || seen.has(tml)) continue;
      seen.add(tml);
      const branch = String(loc['branch'] ?? '').trim();
      const name = String(loc['name'] ?? '').trim();
      rows.push({
        id: tml,
        app_location_id: appId,
        terminal_location_id: tml,
        display_name: branch || name || tml,
        branch,
        name,
        stripe_account_id: accountId,
      });
    }
    return rows;
  }

  /** Merge Stripe Terminal list with any tml_ already stored on hub locations. */
  private mergeTerminalLocationOptions(
    fromApp: StripeConnectLocationRow[],
    fromStripe: StripeConnectLocationRow[]
  ): StripeConnectLocationRow[] {
    const byTml = new Map<string, StripeConnectLocationRow>();
    for (const row of fromApp) {
      const tml = String(row.terminal_location_id || row.id || '').trim();
      if (!/^tml_[a-zA-Z0-9]+$/.test(tml)) continue;
      byTml.set(tml, { ...row, id: tml, terminal_location_id: tml });
    }
    for (const row of fromStripe) {
      const tml = String(row.terminal_location_id || row.id || '').trim();
      if (!/^tml_[a-zA-Z0-9]+$/.test(tml)) continue;
      const existing = byTml.get(tml);
      if (existing) {
        byTml.set(tml, {
          ...existing,
          display_name: existing.display_name || row.display_name || row.name || tml,
          name: existing.name || row.name,
        });
      } else {
        byTml.set(tml, {
          id: tml,
          terminal_location_id: tml,
          display_name: row.display_name || row.name || tml,
          branch: row.branch,
          name: row.name,
          stripe_account_id: this.selectedAccountId,
          app_location_id: row.app_location_id,
        });
      }
    }
    return [...byTml.values()];
  }

  /**
   * When Connect list API is empty/missing, derive accounts from profile locations.
   * Re-runs when `@Input() locations` arrives after ngOnInit (common race).
   */
  private applyLocationDerivedAccounts(): void {
    if (this.accountsSource === 'api' && this.accounts.length > 0) {
      if (this.selectedAccountId) {
        this.lastLoadedLocationsAccountId = '';
        this.loadLocationsForSelectedAccount();
      }
      return;
    }
    const derived = this.buildAccountsFromProfileLocations();
    if (derived.length === 0) {
      if (this.accountsSource !== 'api') {
        this.accounts = [];
        this.accountsSource = 'none';
        this.rebuildAccountOptions();
      }
      return;
    }

    this.accounts = derived;
    this.accountsSource = 'locations';
    this.rebuildAccountOptions();
    if (this.selectedAccountId) {
      this.loadLocationsForSelectedAccount();
    }
  }

  loadAccounts(): void {
    this.loadingAccounts = true;
    this.saveMessage = null;
    this.stripeConnect.listConnectAccounts().subscribe({
      next: fromApi => {
        this.loadingAccounts = false;
        if (fromApi.length > 0) {
          this.accounts = fromApi;
          this.accountsSource = 'api';
          this.rebuildAccountOptions();
          if (this.selectedAccountId) {
            this.loadLocationsForSelectedAccount();
          }
          return;
        }
        this.applyLocationDerivedAccounts();
      },
      error: () => {
        this.loadingAccounts = false;
        this.applyLocationDerivedAccounts();
      },
    });
  }

  formatAccountLabel(a: StripeConnectAccountRow): string {
    const id = this.getStripeAccountId(a);
    const name = (a.display_name || '').trim();
    const email = (a.email || '').trim();
    const primary = name || email;
    if (primary && primary !== id) {
      return `${primary} — ${id}`;
    }
    return id || 'Unknown account';
  }

  formatLocationLabel(l: StripeConnectLocationRow): string {
    const tml = String(l.terminal_location_id || l.id || '').trim();
    const title = (l.display_name || l.branch || l.name || '').trim();
    if (title && tml && title !== tml) {
      return `${title} — ${tml}`;
    }
    return tml || title || 'Unknown location';
  }

  private rebuildAccountOptions(): void {
    this.accountOptions = this.accounts.map(a => ({
      id: this.getStripeAccountId(a),
      label: this.formatAccountLabel(a),
    }));
  }

  private rebuildLocationOptions(): void {
    // Dropdown only lists real Stripe Terminal locations (tml_…).
    this.locationOptions = this.locationsForAccount
      .filter(l => /^tml_[a-zA-Z0-9]+$/.test(String(l.terminal_location_id || l.id || '').trim()))
      .map(l => {
        const tml = String(l.terminal_location_id || l.id || '').trim();
        return {
          id: tml,
          label: this.formatLocationLabel({ ...l, id: tml, terminal_location_id: tml }),
        };
      });
  }

  private getStripeAccountId(account: StripeConnectAccountRow): string {
    return String(account.stripe_account_id || account.id || '').trim();
  }

  onAccountSelected(accountId: string): void {
    const next = String(accountId || '').trim();
    // Ignore Material clearing the value before options are ready.
    if (!next && this.accountOptions.length > 0 && this.selectedAccountId) {
      return;
    }
    if (next === this.selectedAccountId) {
      return;
    }
    this.selectedAccountId = next;
    this.selectedLocationId = '';
    this.pendingLocationId = '';
    this.lastLoadedLocationsAccountId = '';
    this.loadLocationsForSelectedAccount();
  }

  onLocationSelected(locationId: string): void {
    const next = String(locationId || '').trim();
    if (!next && this.locationOptions.length > 0 && this.selectedLocationId) {
      return;
    }
    // Only accept Terminal location ids.
    this.selectedLocationId = /^tml_[a-zA-Z0-9]+$/.test(next) ? next : '';
  }

  loadLocationsForSelectedAccount(): void {
    const accountId = this.selectedAccountId;
    if (!accountId) {
      this.locationsForAccount = [];
      this.locationOptions = [];
      this.lastLoadedLocationsAccountId = '';
      return;
    }

    // Seed from hub locations that already store a tml_ (no plain shop-location fallback).
    const fromApp = this.buildTerminalLocationsFromApp(accountId);
    this.locationsForAccount = fromApp;
    this.rebuildLocationOptions();
    this.applyPendingLocationSelection();

    this.loadingLocations = true;
    this.lastLoadedLocationsAccountId = accountId;
    this.stripeConnect.listLocationsForAccount(accountId).subscribe({
      next: stripeRows => {
        if (this.selectedAccountId !== accountId) {
          return;
        }
        this.loadingLocations = false;
        this.locationsForAccount = this.mergeTerminalLocationOptions(
          this.buildTerminalLocationsFromApp(accountId),
          stripeRows
        );
        this.rebuildLocationOptions();
        this.applyPendingLocationSelection();
      },
      error: () => {
        if (this.selectedAccountId !== accountId) {
          return;
        }
        this.loadingLocations = false;
        this.locationsForAccount = this.buildTerminalLocationsFromApp(accountId);
        this.rebuildLocationOptions();
        this.applyPendingLocationSelection();
      },
    });
  }

  private applyPendingLocationSelection(): void {
    if (!this.locationOptions.length) {
      this.selectedLocationId = '';
      return;
    }
    const ids = new Set(this.locationOptions.map(o => o.id));
    if (this.pendingLocationId && ids.has(this.pendingLocationId)) {
      this.selectedLocationId = this.pendingLocationId;
      this.pendingLocationId = '';
      return;
    }
    if (this.selectedLocationId && ids.has(this.selectedLocationId)) {
      return;
    }
    this.selectedLocationId = '';
  }

  save(): void {
    this.saveMessage = null;
    if (!this.selectedAccountId) {
      this.saveMessage = { type: 'err', text: 'Select a connected account.' };
      return;
    }
    const terminalLocationId = String(this.selectedLocationId || '').trim();
    if (!/^tml_[a-zA-Z0-9]+$/.test(terminalLocationId)) {
      this.saveMessage = {
        type: 'err',
        text: 'Select a Stripe Terminal location (tml_…). Create or link one under Terminal Settings if none are listed.',
      };
      return;
    }
    const selected = this.locationsForAccount.find(
      l => String(l.terminal_location_id || l.id || '').trim() === terminalLocationId
    );
    const appLocationId =
      String(selected?.app_location_id || '').trim() || this.resolveAppLocationId();
    if (!appLocationId) {
      this.saveMessage = {
        type: 'err',
        text: 'Could not resolve the shop location for this Terminal location.',
      };
      return;
    }

    this.persistSelection();
    this.stripeConnect
      .saveLocationPaymentSettings(appLocationId, {
        stripe_account_id: this.selectedAccountId,
        terminal_location_id: terminalLocationId,
      })
      .subscribe(ok => {
        this.saveMessage = ok
          ? { type: 'ok', text: 'Payment settings saved.' }
          : { type: 'err', text: 'Unable to save payment settings. Please try again.' };
      });
  }

  private resolveAppLocationId(): string {
    const accountId = this.selectedAccountId;
    const terminalLocationId = String(this.selectedLocationId || '').trim();
    const matchedByTml = (this.locations || []).find(loc => {
      const tml = String(loc['terminal_location_id'] ?? loc['terminalLocationId'] ?? '').trim();
      return tml === terminalLocationId;
    });
    if (matchedByTml) {
      return String(matchedByTml['id'] ?? '').trim();
    }
    const matchedByAccount = (this.locations || []).find(loc => {
      const locAccountId = String(loc['stripe_account_id'] ?? loc['stripeAccountId'] ?? '').trim();
      return locAccountId === accountId;
    });
    if (matchedByAccount) {
      return String(matchedByAccount['id'] ?? '').trim();
    }
    const activeLocation = (this.locations || []).find(loc => loc['active'] === true);
    return String(activeLocation?.['id'] ?? this.locations?.[0]?.['id'] ?? '').trim();
  }

  trackByAccountId(_: number, a: StripeConnectAccountRow): string {
    return a.id;
  }

  trackByLocationId(_: number, l: StripeConnectLocationRow): string {
    return l.id;
  }
}
